// OAuth 2.0 + PKCE helpers — ported from teliance-chat-portal/src/utils/oauth.js.
// SSO against auth.teliance.com. Verifier/state/returnTo in localStorage
// (survives the cross-subdomain tel_session 302 shortcut, which clears
// sessionStorage on Safari).

const AUTH_BASE = import.meta.env.VITE_AUTH_URL || 'https://auth.teliance.com';
const CLIENT_ID = 'teliance-events';
const SCOPE = [
  'openid', 'profile',
  'events:tenant:liv-miami:admin',
  'events:tenant:gekko-miami:admin',
].join(' ');

const STORAGE_KEYS = {
  verifier: 'oauth_pkce_verifier',
  state: 'oauth_state',
  returnTo: 'oauth_return_to',
};

function base64url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(bytes = 32) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64url(arr);
}

async function sha256(input) {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64url(hash);
}

function getRedirectUri() {
  // MUST match an oauth_clients.redirect_uris row for client_id 'teliance-events':
  //   https://events.teliance.com/auth/callback
  //   http://localhost:5173/auth/callback
  return `${window.location.origin}/auth/callback`;
}

/** Kick off SSO. Stores PKCE verifier + state, redirects to /authorize. */
export async function startSSOLogin(returnTo = '/') {
  const verifier = randomString(32);
  const challenge = await sha256(verifier);
  const state = randomString(16);

  localStorage.setItem(STORAGE_KEYS.verifier, verifier);
  localStorage.setItem(STORAGE_KEYS.state, state);
  localStorage.setItem(STORAGE_KEYS.returnTo, returnTo || '/');

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    scope: SCOPE,
  });
  window.location.assign(`${AUTH_BASE}/authorize?${params.toString()}`);
}

/** Complete SSO at /auth/callback. Validates state, exchanges code for tokens. */
export async function completeSSOLogin(code, state) {
  const storedVerifier = localStorage.getItem(STORAGE_KEYS.verifier);
  const storedState = localStorage.getItem(STORAGE_KEYS.state);
  const returnTo = localStorage.getItem(STORAGE_KEYS.returnTo) || '/';

  const clear = () => {
    localStorage.removeItem(STORAGE_KEYS.verifier);
    localStorage.removeItem(STORAGE_KEYS.state);
    localStorage.removeItem(STORAGE_KEYS.returnTo);
  };

  if (!storedVerifier) { clear(); throw new Error('Missing PKCE verifier, session may have expired. Try signing in again.'); }
  if (!storedState || storedState !== state) { clear(); throw new Error('OAuth state mismatch, possible CSRF. Try signing in again.'); }
  clear(); // single-use

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    code_verifier: storedVerifier,
    client_id: CLIENT_ID,
    redirect_uri: getRedirectUri(),
  });

  const resp = await fetch(`${AUTH_BASE}/token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error_description || data.error || 'Token exchange failed');

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
    returnTo,
  };
}

/** Decode JWT claims without verifying (UI pre-population; server re-verifies). */
export function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
